import { useEffect } from 'react'
import PropTypes from 'prop-types'
import { X } from 'lucide-react'

/**
 * Shared right-docked node-detail panel shell used by every knowledge-graph surface
 * (student `NodeDetailPanel`, tutor `EducatorNodeDetailPanel`, admin `AdminNodeDetailPanel`).
 *
 * It owns ONLY the chrome: the right-dock anchoring, the fixed header (breadcrumb / title / badge
 * row / close), the tab bar, the scrolling body, and Escape-to-close. Each surface passes its own
 * header content, tabs, and body so the role-specific logic stays in the role's component.
 *
 * Anchoring: `absolute inset-y-0 right-0` INSIDE the page's relative body region (never `fixed`),
 * so it docks to the right of the graph pane and never covers the top nav / toolbar / legend
 * footer. Width is the shared `--panel-w` CSS var the host page sets (and reserves on the canvas
 * with `xl:mr-[var(--panel-w)]`); the 30rem fallback keeps a sane width if rendered without it. On
 * narrow screens (below the host's `xl` reservation) it overlays full-width. A flex column keeps the
 * header + tab bar fixed while the body scrolls.
 *
 * @param {Object}   [tint]        - header accent, `{ bg, accent }` Tailwind class strings
 * @param {ReactNode} [breadcrumb] - small breadcrumb line above the title (e.g. "Advanced Math | Math")
 * @param {ReactNode} title        - node title (string, or a node for a loading skeleton)
 * @param {ReactNode} [badges]     - the badge row under the title (status / DOK / difficulty, etc.)
 * @param {Function}  onClose      - close the panel (also bound to Escape)
 * @param {Array}     [tabs]       - `[{ id, label, Icon }]`; omit/empty to hide the tab bar
 * @param {string}    [activeTab]  - id of the active tab
 * @param {Function}  [onTabChange]- (id) => void
 * @param {ReactNode} children     - the active tab's body (each surface supplies its own padding)
 * @param {string}    [className]  - extra classes on the root
 */
export default function NodePanelShell({
  tint,
  breadcrumb,
  title,
  badges,
  onClose,
  tabs = [],
  activeTab,
  onTabChange,
  children,
  className = ''
}) {
  // Escape-to-close, centralized here so every surface behaves the same.
  useEffect(() => {
    if (!onClose) return undefined
    const onKey = (e) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div
      className={`absolute inset-y-0 right-0 z-40 flex w-full max-w-[var(--panel-w,30rem)] min-h-[24rem] flex-col bg-white dark:bg-gray-900 shadow-2xl border-l border-gray-200 dark:border-gray-700 ${className}`}
      role="dialog"
      aria-modal="false"
    >
      {/* Header (fixed) */}
      <div
        className={`flex items-start justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0 ${tint?.bg || ''}`}
      >
        <div className="flex-1 min-w-0">
          {breadcrumb != null && (
            <div
              className={`text-xs font-medium mb-1 ${tint?.accent || 'text-gray-500 dark:text-gray-400'}`}
            >
              {breadcrumb}
            </div>
          )}
          {typeof title === 'string' ? (
            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">{title}</h2>
          ) : (
            title
          )}
          {badges && <div className="flex items-center flex-wrap gap-2 mt-2">{badges}</div>}
        </div>
        <button
          onClick={onClose}
          className="ml-2 shrink-0 p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Tab bar (fixed). overflow-x-auto + whitespace-nowrap lets 4-5 tabs (admin) scroll on a
          narrow panel while 2-3 short tabs (student/tutor) still flex to fill the width. */}
      {tabs.length > 0 && (
        <div className="flex border-b border-gray-200 dark:border-gray-700 shrink-0 overflow-x-auto">
          {tabs.map(({ id, label, Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => onTabChange?.(id)}
              className={`flex-1 whitespace-nowrap inline-flex items-center justify-center gap-1.5 px-3 py-2.5 text-sm font-medium transition-colors ${
                activeTab === id
                  ? 'text-primary-600 border-b-2 border-primary-600'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              {Icon && <Icon className="w-4 h-4" />}
              {label}
            </button>
          ))}
        </div>
      )}

      {/* Body (scrolls) */}
      <div className="flex-1 min-h-0 overflow-y-auto">{children}</div>
    </div>
  )
}

NodePanelShell.propTypes = {
  tint: PropTypes.shape({ bg: PropTypes.string, accent: PropTypes.string }),
  breadcrumb: PropTypes.node,
  title: PropTypes.node.isRequired,
  badges: PropTypes.node,
  onClose: PropTypes.func.isRequired,
  tabs: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.string, label: PropTypes.node, Icon: PropTypes.elementType })
  ),
  activeTab: PropTypes.string,
  onTabChange: PropTypes.func,
  children: PropTypes.node,
  className: PropTypes.string
}
